import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Layers, ListChecks, Search, ChevronRight, Unlock, Calendar, Lock } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { EmptyState, PageHeader } from "../ui";
import "./StudentTestSeries.scss";

const UNLOCK_ICONS = { open: Unlock, scheduled: Calendar, sequential: Lock };

const StudentTestSeriesPage = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchSeries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/student-test-series");
      setSeries(data.series || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSeries(); }, [fetchSeries]);

  const filtered = series.filter((s) =>
    !search || s.title.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;

  return (
    <div className="student-test-series-page">
      <PageHeader title="Test Series" description="Your test series & mock packs" icon={Layers} />

      <div className="sts-toolbar">
        <div className="position-relative flex-grow-1" style={{ minWidth: 200 }}>
          <Search size={14} className="position-absolute top-50 translate-middle-y ms-2" style={{ color: "var(--text-tertiary)" }} />
          <input className="sts-search ps-4 w-100" placeholder="Search test series..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<Layers size={40} />} title="No test series"
          description={search ? "No series match your search" : "No test series assigned to you yet"} />
      ) : (
        <div className="sts-grid">
          {filtered.map((s) => {
            const UnlockIcon = UNLOCK_ICONS[s.unlockMode] || Unlock;
            return (
              <button key={s._id} type="button" className="sts-card" onClick={() => navigate(`/s/test-series/${s._id}`)}>
                <div className="sts-card__top">
                  <div className="sts-card__icon"><Layers size={18} /></div>
                  {s.category && <span className="sts-card__cat">{s.category}</span>}
                </div>
                <div className="sts-card__title">{s.title}</div>
                {s.description && <div className="sts-card__desc">{s.description}</div>}

                <div className="sts-progress">
                  <div className="sts-progress__bar">
                    <span style={{ width: `${s.progressPct || 0}%` }} />
                  </div>
                  <div className="sts-progress__label">
                    {s.completedTests}/{s.totalTests} done · {s.progressPct || 0}%
                  </div>
                </div>

                <div className="sts-card__foot">
                  <span className="sts-meta"><ListChecks size={13} /> {s.totalTests} tests</span>
                  <span className="sts-meta"><UnlockIcon size={13} /> {s.unlockMode}</span>
                  <ChevronRight size={16} className="ms-auto" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentTestSeriesPage;
